import {
    GetIngredientsDocument,
    type GetIngredientsQuery,
    type GetIngredientsQueryVariables,
    type Recipe,
    RecipeCategory,
    type RecipeFilter,
    type RecipeSearchInput,
    RecipesSorting,
    RecipeType,
    SearchRecipesDocument,
    type SearchRecipesQuery,
    type SearchRecipesQueryVariables,
    TagType,
} from 'src/graphql/generated/operations';
import { RecipeListCard, formatRecipeTotalTime } from '@mealmanager/recipe-card-ui';
import { getCollapsibleRecipeMacros } from '@mealmanager/nutrition-ui';
import { useQuery } from '@apollo/client/react';
import { Card, Row, Col, Input, InputNumber, Select, Space, Button, Grid, Typography, theme, Dropdown, Modal, Drawer, Divider, type MenuProps } from 'antd';
import { memo, useState, useEffect, useMemo, useCallback, type CSSProperties, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import Loading from '~/components/Loading';
import { GetEnumKeys, formatDisplayedTagName, getVisibleRecipeCategoryOptions } from 'src/graphql/utils';
import { useLocation, useNavigate, useRouteLoaderData, useSearchParams } from 'react-router';
import { useScrollRestoration } from '@mealmanager/scroll-restoration-hooks';
import { RecipeImageFlex } from '../components/graphics/RecipeImage';
import { parseIdListParam, parseRecipeCategory, parseRecipeType } from '../routes/recipes.helpers';
import RatingIndicator from '../components/graphics/RatingIndicator';
import CardCornerLabel from '~/components/graphics/CardCornerLabel';
import EmptyStatePage from '~/components/EmptyState';
import { ClockCircleOutlined, FilterOutlined, SearchOutlined, SortAscendingOutlined } from '@ant-design/icons';
import PagePadding from '~/components/PagePadding';
import { sageCreamPalette } from '../components/AppShell/Theme';

const DEFAULT_SORT = RecipesSorting.MostRelevant;
const MIN_SEARCH_LENGTH = 3;
const REAL_OVORE_SCORE_MAX = 100;

const SORT_OPTIONS: Array<{ value: RecipesSorting; label: string }> = [
    { value: RecipesSorting.MostRelevant, label: 'Most relevant' },
    { value: RecipesSorting.CreatedAtDesc, label: 'Newest first' },
    { value: RecipesSorting.CreatedAtAsc, label: 'Oldest first' },
    { value: RecipesSorting.NameAsc, label: 'Name A-Z' },
    { value: RecipesSorting.NameDesc, label: 'Name Z-A' },
    { value: RecipesSorting.RatingDesc, label: 'Rating high to low' },
    { value: RecipesSorting.RatingAsc, label: 'Rating low to high' },
    { value: RecipesSorting.NetCarbsPercentageAsc, label: 'Net carbs % low to high' },
    { value: RecipesSorting.NetCarbsPercentageDesc, label: 'Net carbs % high to low' },
    { value: RecipesSorting.CaloriesAsc, label: 'Calories low to high' },
    { value: RecipesSorting.CaloriesDesc, label: 'Calories high to low' },
    { value: RecipesSorting.UpdatedAtDesc, label: 'Recently updated' },
    { value: RecipesSorting.UpdatedAtAsc, label: 'Least recently updated' },
];

function parseScoreParam(searchParams: URLSearchParams, key: string): number | undefined {
    const rawValue = searchParams.get(key);
    if (!rawValue?.trim()) return undefined;

    const value = Number(rawValue);
    return Number.isFinite(value) ? Math.min(Math.max(value, 0), REAL_OVORE_SCORE_MAX) : undefined;
}

export function buildFilterFromSearchParams(searchParams: URLSearchParams): RecipeFilter {
    const initialSortParam = searchParams.get('sort');
    const initialSortFromParams = initialSortParam ? SORT_OPTIONS.find((option) => option.value === initialSortParam)?.value : undefined;
    const initialSort = initialSortFromParams ?? DEFAULT_SORT;
    const nameSearch = searchParams.get('nameSearch')?.trim() ?? '';

    const realOVoreScoreMin = parseScoreParam(searchParams, 'realOVoreScoreMin');
    const realOVoreScoreMax = parseScoreParam(searchParams, 'realOVoreScoreMax');

    return {
        hasIngredients: parseIdListParam(searchParams.get('hasIngredients')),
        category: parseRecipeCategory(searchParams.get('category')),
        type: parseRecipeType(searchParams.get('type')),
        cuisineTags: parseIdListParam(searchParams.get('cuisineTags')),
        customTags: parseIdListParam(searchParams.get('customTags')),
        ...(nameSearch.length >= MIN_SEARCH_LENGTH ? { nameContains: nameSearch } : {}),
        ...((realOVoreScoreMin !== undefined || realOVoreScoreMax !== undefined) ? {
            realOVoreScore: {
                ...(realOVoreScoreMin !== undefined ? { min: realOVoreScoreMin } : {}),
                ...(realOVoreScoreMax !== undefined ? { max: realOVoreScoreMax } : {}),
            },
        } : {}),
        sort: initialSort,
    } as RecipeFilter;
}

export function buildSearchInput(filter: RecipeFilter): RecipeSearchInput {
    return {
        category: filter.category,
        ingredientIDs: filter.hasIngredients,
        cuisineTagIDs: filter.cuisineTags,
        customTagIDs: filter.customTags,
        query: filter.nameContains?.trim() || '',
        nutrition: filter.realOVoreScore ? { realOVoreScore: filter.realOVoreScore } : undefined,
        sort: filter.sort ?? DEFAULT_SORT,
        type: filter.type,
    };
}

export function buildTagOptions(recipes: Recipe[], tagType: TagType): Array<{ value: string; label: string }> {
    return Array.from(new Map(recipes
        .flatMap((recipe) => recipe.tags || [])
        .filter((recipeTag) => recipeTag.tag?.type === tagType)
        .map((recipeTag) => [recipeTag.tag.id, { value: recipeTag.tag.id, label: formatDisplayedTagName(recipeTag.tag.name) }]))
        .values());
}

export function countActiveFilters(filter: RecipeFilter): number {
    let count = 0;
    if (filter.category) count += 1;
    if (filter.type) count += 1;
    if (filter.hasIngredients?.length) count += 1;
    if (filter.cuisineTags?.length) count += 1;
    if (filter.customTags?.length) count += 1;
    if (filter.realOVoreScore) count += 1;
    return count;
}

const getRecipeTotalDuration = formatRecipeTotalTime;

function formatRecipeCuisineMeta(recipe: Pick<Recipe, 'tags'>): string | null {
    const cuisines = recipe.tags
        ?.filter((tag) => tag.tag.type === TagType.Cuisine)
        .map((tag) => formatDisplayedTagName(tag.tag.name))
        .filter(Boolean);

    if (!cuisines?.length) return null;

    return cuisines.join(', ');
}

const softPillStyle: CSSProperties = {
    border: `1px solid ${sageCreamPalette.border}`,
    background: sageCreamPalette.sagePale,
    color: sageCreamPalette.sageDark,
    margin: 0,
};

type RecipeGridCardProps = {
    recipe: Recipe;
    isLg: boolean;
    token: ReturnType<typeof theme.useToken>['token'];
    isMacrosExpanded: boolean;
    onToggleMacros: (recipeId: string) => void;
};

const RecipeGridCard = memo(function RecipeGridCard({
    recipe,
    isLg,
    token,
    isMacrosExpanded,
    onToggleMacros,
}: RecipeGridCardProps) {
    const totalDuration = getRecipeTotalDuration(recipe);
    const cuisineMeta = formatRecipeCuisineMeta(recipe);
    const { button: macrosButton, panel: macrosPanel } = getCollapsibleRecipeMacros({
        hideByDefault: true,
        open: isMacrosExpanded,
        onToggle: () => onToggleMacros(recipe.id),
        testId: `recipe-card-macros-${recipe.id}`,
        calories: recipe.calories,
        netCarbs: recipe.netCarbs,
        proteins: recipe.proteins,
        fat: recipe.fat,
        fibers: recipe.fibers,
        token,
    });

    return (
        <Col xs={24} lg={12} key={recipe.id} style={{ display: 'flex' }}>
            <RecipeListCard
                title={recipe.name}
                href={`/recipes/${recipe.id}`}
                compact={!isLg}
                cardBackground={sageCreamPalette.cardBg}
                isNew={!!(recipe.createdAt && new Date(recipe.createdAt).getTime() > Date.now() - 14 * 24 * 60 * 60 * 1000)}
                newBadge={(
                    <CardCornerLabel
                        bgColor={sageCreamPalette.sage}
                        color="white"
                        text="NEW"
                    />
                )}
                image={(
                    <div style={{ width: isLg ? 160 : 108, height: isLg ? 145 : 108, overflow: 'hidden', borderRadius: 16, background: sageCreamPalette.sagePale }}>
                        <RecipeImageFlex recipe={recipe} />
                    </div>
                )}
                meta={(
                    <Space wrap size={[6, 4]}>
                        <RatingIndicator recipe={recipe} />
                        {totalDuration ? (
                            <Typography.Text type="secondary" aria-hidden="true" style={{ fontSize: 13 }}>
                                ·
                            </Typography.Text>
                        ) : null}
                        {totalDuration ? (
                            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                                <ClockCircleOutlined /> {totalDuration}
                            </Typography.Text>
                        ) : null}
                        {totalDuration && cuisineMeta ? (
                            <Typography.Text type="secondary" aria-hidden="true" style={{ fontSize: 13 }}>
                                ·
                            </Typography.Text>
                        ) : null}
                        {cuisineMeta ? (
                            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                                {cuisineMeta}
                            </Typography.Text>
                        ) : null}
                    </Space>
                )}
                tags={[]}
                tagsRowExtra={macrosButton}
                detailsRow={macrosPanel}
                tagContainerTestId={`recipe-card-tags-${recipe.id}`}
                rating={null}
            />
        </Col>
    );
});

type RecipeSearchCardProps = {
    activeFilterCount: number;
    appliedSearchText: string;
    isLg: boolean;
    onOpenFilters: () => void;
    onSubmitSearch: (nextSearch: string) => void;
    onSortSelect: MenuProps['onClick'];
    selectedSortLabel: string;
    sortMenuItems: MenuProps['items'];
    sortSelectedKey: RecipesSorting;
    token: ReturnType<typeof theme.useToken>['token'];
};

const RecipeSearchCard = memo(function RecipeSearchCard({
    activeFilterCount,
    appliedSearchText,
    isLg,
    onOpenFilters,
    onSubmitSearch,
    onSortSelect,
    selectedSortLabel,
    sortMenuItems,
    sortSelectedKey,
    token,
}: RecipeSearchCardProps) {
    const [draftSearch, setDraftSearch] = useState(appliedSearchText);

    useEffect(() => {
        setDraftSearch(appliedSearchText);
    }, [appliedSearchText]);

    const filterButtonLabel = activeFilterCount > 0 ? `Filters (${activeFilterCount})` : 'Filters';
    const actionButtonStyle = {
        width: 44,
        height: 44,
        borderRadius: 22,
    } as const;

    const desktopAction = (key: string, label: string, button: ReactNode) => (
        <div key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 56 }}>
            {button}
            <Typography.Text style={{ fontSize: 12, lineHeight: 1.1 }}>{label}</Typography.Text>
        </div>
    );

    return (
        <Card
            className="shadowed-card"
            style={{ marginBottom: isLg ? 20 : 14, borderRadius: 22, background: sageCreamPalette.cardBg }}
            styles={{ body: { padding: isLg ? 18 : 12 } }}
        >
            <Space orientation="vertical" size={isLg ? 18 : 12} style={{ width: '100%' }}>
                {isLg ? (
                    <Row gutter={[16, 16]} align="middle" wrap>
                        <Col flex="auto" style={{ minWidth: 280 }}>
                            <Input
                                placeholder="Search recipes, ingredients, cuisines, tags..."
                                allowClear
                                prefix={<SearchOutlined style={{ color: token.colorTextTertiary, fontSize: 18 }} />}
                                value={draftSearch}
                                onChange={(e) => {
                                    const nextSearch = e.target.value;
                                    setDraftSearch(nextSearch);
                                    if (nextSearch.length === 0) onSubmitSearch('');
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') onSubmitSearch(draftSearch);
                                }}
                                style={{
                                    height: 52,
                                    borderRadius: 18,
                                    fontSize: 16,
                                    paddingInline: 18,
                                    background: sageCreamPalette.cardBgMuted,
                                }}
                            />
                        </Col>
                        <Col>
                            <Space size={12} align="start">
                                {desktopAction(
                                    'search',
                                    'Search',
                                    <Button
                                        style={actionButtonStyle}
                                        onClick={() => onSubmitSearch(draftSearch)}
                                        aria-label="Search recipes"
                                        icon={<SearchOutlined />}
                                    />
                                )}
                                {desktopAction(
                                    'filters',
                                    'Filters',
                                    <Button
                                        style={actionButtonStyle}
                                        onClick={onOpenFilters}
                                        aria-label={filterButtonLabel}
                                        icon={<FilterOutlined />}
                                    />
                                )}
                                {desktopAction(
                                    'sort',
                                    'Sort',
                                    <Dropdown
                                        menu={{
                                            items: sortMenuItems,
                                            onClick: onSortSelect,
                                            selectable: true,
                                            selectedKeys: [sortSelectedKey],
                                        }}
                                        trigger={['click']}
                                    >
                                        <Button
                                            style={actionButtonStyle}
                                            aria-label={`Sort recipes. Current order: ${selectedSortLabel}`}
                                            icon={<SortAscendingOutlined />}
                                        />
                                    </Dropdown>
                                )}
                            </Space>
                        </Col>
                    </Row>
                ) : (
                    <Space orientation="vertical" size={12} style={{ width: '100%' }}>
                        <Input
                            placeholder="Search recipes..."
                            allowClear
                            prefix={<SearchOutlined style={{ color: token.colorTextTertiary, fontSize: 18 }} />}
                            value={draftSearch}
                            onChange={(e) => {
                                const nextSearch = e.target.value;
                                setDraftSearch(nextSearch);
                                if (nextSearch.length === 0) onSubmitSearch('');
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') onSubmitSearch(draftSearch);
                            }}
                            style={{
                                height: 48,
                                borderRadius: 16,
                                fontSize: 16,
                                paddingInline: 16,
                                background: sageCreamPalette.cardBgMuted,
                            }}
                        />
                        <Row gutter={[10, 10]} wrap>
                            <Col xs={12} sm={8}>
                                <Button block icon={<SearchOutlined />} onClick={() => onSubmitSearch(draftSearch)} aria-label="Search recipes">Search</Button>
                            </Col>
                            <Col xs={12} sm={8}>
                                <Button block icon={<FilterOutlined />} onClick={onOpenFilters}>{filterButtonLabel}</Button>
                            </Col>
                            <Col xs={12} sm={8}>
                                <Dropdown
                                    menu={{
                                        items: sortMenuItems,
                                        onClick: onSortSelect,
                                        selectable: true,
                                        selectedKeys: [sortSelectedKey],
                                    }}
                                    trigger={['click']}
                                >
                                    <Button block icon={<SortAscendingOutlined />}>Sort</Button>
                                </Dropdown>
                            </Col>
                        </Row>
                    </Space>
                )}
            </Space>
        </Card>
    );
});

export default function Page() {
    const routeData = useRouteLoaderData('routes/recipes') as { recipes?: Recipe[] | null } | undefined;
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { token } = theme.useToken();
    const [filter, setFilter] = useState<RecipeFilter>(() => buildFilterFromSearchParams(searchParams));
    const initialRecipeSearch = searchParams.get('nameSearch') || '';
    const [appliedSearchParam, setAppliedSearchParam] = useState<string>(
        initialRecipeSearch.trim().length >= MIN_SEARCH_LENGTH ? initialRecipeSearch.trim() : ''
    );
    const [ingredientsData, setIngredientsData] = useState<Array<{ value: string, label: string }>>([]);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [expandedMacroRecipeIds, setExpandedMacroRecipeIds] = useState<Set<string>>(new Set());
    const toggleMacrosExpanded = useCallback((recipeId: string) => {
        setExpandedMacroRecipeIds((prev) => {
            const next = new Set(prev);
            if (next.has(recipeId)) {
                next.delete(recipeId);
            } else {
                next.add(recipeId);
            }
            return next;
        });
    }, []);
    const [draftFilter, setDraftFilter] = useState<RecipeFilter>(filter);

    const categories = getVisibleRecipeCategoryOptions();
    const recipeTypes = GetEnumKeys(RecipeType).map((key) => (
        { value: RecipeType[key].toString(), label: key.toString() }
    ));
    const sortSelectedKey = filter.sort ?? DEFAULT_SORT;
    const selectedSortOption = SORT_OPTIONS.find((option) => option.value === sortSelectedKey)
        ?? SORT_OPTIONS.find((option) => option.value === DEFAULT_SORT)!;
    const sortMenuItems: MenuProps['items'] = SORT_OPTIONS.map((option) => ({
        key: option.value,
        label: <span style={{ color: option.value === sortSelectedKey ? token.colorTextBase : undefined }}>{option.label}</span>,
    }));
    const screens = Grid.useBreakpoint();
    const isLg = !!screens.lg;
    const searchInput = useMemo(() => buildSearchInput(filter), [filter]);

    const handleSortSelect: MenuProps['onClick'] = ({ key }) => {
        const sortKey = SORT_OPTIONS.find((option) => option.value === key)?.value ?? DEFAULT_SORT;
        setFilter((previousFilter) => {
            if (previousFilter.sort === sortKey) return previousFilter;
            return { ...previousFilter, sort: sortKey };
        });
    };

    const { data: ingredientData } = useQuery<GetIngredientsQuery, GetIngredientsQueryVariables>(GetIngredientsDocument, {
        fetchPolicy: 'cache-and-network',
        skip: ingredientsData.length > 0,
    });

    useEffect(() => {
        if (ingredientData?.ingredients) {
            setIngredientsData(
                ingredientData.ingredients.map((ingredient) => ({
                    value: ingredient.id,
                    label: ingredient.name
                }))
            );
        }
    }, [ingredientData]);

    const { loading, data } = useQuery<SearchRecipesQuery, SearchRecipesQueryVariables>(
        SearchRecipesDocument,
        {
            fetchPolicy: routeData?.recipes?.length ? 'cache-and-network' : 'network-only',
            variables: {
                input: searchInput,
            }
        }
    );

    const hydratedRecipes = data?.searchRecipes.recipes ?? routeData?.recipes ?? [];
    const isLoading = loading && hydratedRecipes.length === 0;
    useScrollRestoration({ containerId: 'app-shell-content', ready: !isLoading });
    const cuisineTagOptions = buildTagOptions(hydratedRecipes, TagType.Cuisine);
    const customTagOptions = buildTagOptions(hydratedRecipes, TagType.Custom);
    const activeFilterCount = countActiveFilters(filter);

    function submitSearch(nextSearch: string) {
        const trimmedSearch = nextSearch.trim();
        const appliedSearch = trimmedSearch.length >= MIN_SEARCH_LENGTH ? trimmedSearch : '';
        setAppliedSearchParam(appliedSearch);
        setFilter((previousFilter) => {
            if (appliedSearch) {
                if (previousFilter.nameContains === appliedSearch) return previousFilter;
                return { ...previousFilter, nameContains: appliedSearch };
            }
            if (!previousFilter.nameContains) return previousFilter;
            const { nameContains: _nameContains, ...rest } = previousFilter;
            return rest;
        });
    }

    function resetFilters() {
        setFilter({ sort: DEFAULT_SORT });
        setAppliedSearchParam('');
    }

    function clearDraftFilters() {
        setDraftFilter({ sort: DEFAULT_SORT });
    }

    function openFilters() {
        setDraftFilter(filter);
        setFiltersOpen(true);
    }

    function applyDraftFilters() {
        setFilter((previousFilter) => ({
            ...draftFilter,
            nameContains: previousFilter.nameContains,
            sort: draftFilter.sort ?? previousFilter.sort ?? DEFAULT_SORT,
        }));
        setFiltersOpen(false);
    }

    function renderFilterEditor(currentFilter: RecipeFilter, setCurrentFilter: Dispatch<SetStateAction<RecipeFilter>>) {
        return (
            <Row gutter={[12, 12]}>
                <Col xs={24} md={12}>
                    <Typography.Text type="secondary">Meal</Typography.Text>
                    <Select
                        placeholder="Select meal"
                        options={categories}
                        onChange={(selected) => {
                            const text = (typeof selected === 'string' ? selected : (selected as { value?: string } | undefined)?.value);
                            setCurrentFilter((previous) => ({
                                ...previous,
                                category: (text || undefined) as RecipeCategory | undefined,
                            }));
                        }}
                        value={currentFilter.category ? { value: currentFilter.category, label: currentFilter.category } : undefined}
                        style={{ width: '100%', marginTop: 6 }}
                        allowClear
                    />
                </Col>
                <Col xs={24} md={12}>
                    <Typography.Text type="secondary">Dietary preference</Typography.Text>
                    <Select
                        placeholder="Select dietary preference"
                        options={recipeTypes}
                        onChange={(selected) => {
                            const text = (typeof selected === 'string' ? selected : (selected as { value?: string } | undefined)?.value);
                            setCurrentFilter((previous) => ({
                                ...previous,
                                type: (text || undefined) as RecipeType | undefined,
                            }));
                        }}
                        value={currentFilter.type ? { value: currentFilter.type, label: currentFilter.type } : undefined}
                        style={{ width: '100%', marginTop: 6 }}
                        allowClear
                    />
                </Col>
                <Col xs={24} md={12}>
                    <Typography.Text type="secondary">Cuisine</Typography.Text>
                    <Select
                        mode="multiple"
                        showSearch
                        optionFilterProp="label"
                        filterOption={(input, option) => {
                            const text = (typeof option?.label === 'string' ? option.label : '');
                            return text.toLowerCase().includes(input.toLowerCase());
                        }}
                        placeholder="Select cuisine"
                        options={cuisineTagOptions}
                        value={currentFilter.cuisineTags}
                        onChange={(values: Array<string | { value: string; label: string }>) => {
                            const cuisineTags = Array.isArray(values) ? values.map((value) => typeof value === 'string' ? value : value.value) : [];
                            setCurrentFilter((previous) => ({ ...previous, cuisineTags }));
                        }}
                        style={{ width: '100%', marginTop: 6 }}
                        allowClear
                    />
                </Col>
                <Col xs={24} md={12}>
                    <Typography.Text type="secondary">Custom tags</Typography.Text>
                    <Select
                        mode="multiple"
                        showSearch
                        optionFilterProp="label"
                        filterOption={(input, option) => {
                            const text = (typeof option?.label === 'string' ? option.label : '');
                            return text.toLowerCase().includes(input.toLowerCase());
                        }}
                        placeholder="Select custom tags"
                        options={customTagOptions}
                        value={currentFilter.customTags}
                        onChange={(values: Array<string | { value: string; label: string }>) => {
                            const customTags = Array.isArray(values) ? values.map((value) => typeof value === 'string' ? value : value.value) : [];
                            setCurrentFilter((previous) => ({ ...previous, customTags }));
                        }}
                        style={{ width: '100%', marginTop: 6 }}
                        allowClear
                    />
                </Col>
                <Col xs={24} md={12}>
                    <Typography.Text type="secondary">Whole food</Typography.Text>
                    <Space.Compact style={{ width: '100%', marginTop: 6 }}>
                        <InputNumber
                            min={0}
                            max={REAL_OVORE_SCORE_MAX}
                            placeholder="Min"
                            value={currentFilter.realOVoreScore?.min ?? null}
                            onChange={(min) => setCurrentFilter((previous) => ({
                                ...previous,
                                realOVoreScore: { ...previous.realOVoreScore, ...(typeof min === 'number' ? { min } : {}) },
                            }))}
                        />
                        <InputNumber
                            min={0}
                            max={REAL_OVORE_SCORE_MAX}
                            placeholder="Max"
                            value={currentFilter.realOVoreScore?.max ?? null}
                            onChange={(max) => setCurrentFilter((previous) => ({
                                ...previous,
                                realOVoreScore: { ...previous.realOVoreScore, ...(typeof max === 'number' ? { max } : {}) },
                            }))}
                        />
                    </Space.Compact>
                </Col>
                <Col xs={24}>
                    <Typography.Text type="secondary">Ingredients</Typography.Text>
                    <Select
                        mode="multiple"
                        showSearch
                        optionFilterProp="label"
                        filterOption={(input, option) => {
                            const text = (typeof option?.label === 'string' ? option.label : '');
                            return text.toLowerCase().includes(input.toLowerCase());
                        }}
                        placeholder="Select ingredients"
                        options={ingredientsData}
                        onChange={(values) => {
                            const arr = Array.isArray(values) ? values : [];
                            setCurrentFilter((previous) => ({
                                ...previous,
                                hasIngredients: arr as string[]
                            }));
                        }}
                        value={currentFilter.hasIngredients}
                        style={{ width: '100%', marginTop: 6 }}
                        allowClear
                    />
                </Col>
            </Row>
        );
    }

    useEffect(() => {
        const urlParams = new URLSearchParams();
        if (filter.hasIngredients?.length) {
            urlParams.set('hasIngredients', filter.hasIngredients.join(','));
        }
        if (filter.category) {
            urlParams.set('category', filter.category.toString());
        }
        if (filter.type) {
            urlParams.set('type', filter.type.toString());
        }
        if (filter.cuisineTags && filter.cuisineTags.length > 0) {
            urlParams.set('cuisineTags', filter.cuisineTags.join(','));
        }
        if (filter.customTags && filter.customTags.length > 0) {
            urlParams.set('customTags', filter.customTags.join(','));
        }
        if (filter.nameContains) {
            urlParams.set('nameSearch', filter.nameContains);
        }
        if (filter.sort && filter.sort !== DEFAULT_SORT) {
            urlParams.set('sort', filter.sort);
        }
        if (filter.realOVoreScore?.min !== undefined) {
            urlParams.set('realOVoreScoreMin', String(filter.realOVoreScore.min));
        }
        if (filter.realOVoreScore?.max !== undefined) {
            urlParams.set('realOVoreScoreMax', String(filter.realOVoreScore.max));
        }
        const newSearch = urlParams.toString();
        const currentSearch = location.search.startsWith('?') ? location.search.slice(1) : location.search;
        if (newSearch !== currentSearch) {
            navigate(`/recipes${newSearch ? `?${newSearch}` : ''}`, { replace: true });
        }
    }, [filter, location.search, navigate]);

    return (
        <>
            <PagePadding style={{ background: sageCreamPalette.pageBg }}>
                <Row align="middle" justify="space-between" style={{ marginBottom: isLg ? 18 : 12, gap: 12 }}>
                    <Col flex="auto" style={{ minWidth: 0 }}>
                        <Typography.Title className="display-heading app-page-title" level={1} style={{ margin: 0 }}>
                            Recipes
                        </Typography.Title>
                    </Col>
                </Row>
                <RecipeSearchCard
                    activeFilterCount={activeFilterCount}
                    appliedSearchText={appliedSearchParam}
                    isLg={isLg}
                    onOpenFilters={openFilters}
                    onSubmitSearch={submitSearch}
                    onSortSelect={handleSortSelect}
                    selectedSortLabel={selectedSortOption.label}
                    sortMenuItems={sortMenuItems}
                    sortSelectedKey={sortSelectedKey}
                    token={token}
                />
                {isLg ? (
                    <Modal
                        open={filtersOpen}
                        onCancel={() => setFiltersOpen(false)}
                        footer={null}
                        title="Filters"
                        width={760}
                    >
                        <Space orientation="vertical" size={18} style={{ width: '100%' }}>
                            {renderFilterEditor(draftFilter, setDraftFilter)}
                            <Row justify="space-between" align="middle">
                                <Col>
                                    <Button type="link" onClick={clearDraftFilters} style={{ paddingLeft: 0, color: token.colorTextSecondary }}>Clear all</Button>
                                </Col>
                                <Col>
                                    <Space>
                                        <Button onClick={() => setFiltersOpen(false)}>Cancel</Button>
                                        <Button type="primary" onClick={applyDraftFilters}>Apply</Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Space>
                    </Modal>
                ) : (
                    <Drawer
                        open={filtersOpen}
                        onClose={() => setFiltersOpen(false)}
                        title="Filters"
                        placement="bottom"
                        size="82vh"
                    >
                        <Space orientation="vertical" size={18} style={{ width: '100%' }}>
                            {renderFilterEditor(draftFilter, setDraftFilter)}
                            <Divider style={{ margin: 0 }} />
                            <Row justify="space-between" align="middle">
                                <Col>
                                    <Button type="link" onClick={clearDraftFilters} style={{ paddingLeft: 0, color: token.colorTextSecondary }}>Clear all</Button>
                                </Col>
                                <Col>
                                    <Button type="primary" onClick={applyDraftFilters}>Apply</Button>
                                </Col>
                            </Row>
                        </Space>
                    </Drawer>
                )}
                {isLoading ? (
                    <Loading />
                ) : (
                    <>
                        {hydratedRecipes.length > 0 ? (
                            <Row gutter={isLg ? [18, 18] : [10, 10]} style={{ alignItems: 'stretch' }}>
                                {hydratedRecipes.map((recipe) => (
                                    <RecipeGridCard
                                        key={recipe.id}
                                        recipe={recipe}
                                        isLg={isLg}
                                        token={token}
                                        isMacrosExpanded={expandedMacroRecipeIds.has(recipe.id)}
                                        onToggleMacros={toggleMacrosExpanded}
                                    />
                                ))}
                            </Row>
                        ) : (
                            <EmptyStatePage
                                title="No Recipes Found"
                                message="The filters you have applied did not return any results."
                                linkText="Clear Filters"
                                onLinkClick={resetFilters}
                            />
                        )}
                    </>
                )}
            </PagePadding>
        </>
    );
}
